<template>
  <Dialog :open="isOpen" @update:open="$emit('update:isOpen', $event)">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Visibility Settings</DialogTitle>
        <DialogDescription>
          Toggle different visibility features for development and testing.
        </DialogDescription>
      </DialogHeader>
      <div class="space-y-4 py-4">
        <div
          v-for="setting in settings"
          :key="setting.id"
          class="flex items-center justify-between">
          <div class="space-y-0.5">
            <label :for="setting.id" class="text-sm font-medium cursor-pointer">{{ setting.label }}</label>
            <p class="text-sm text-muted-foreground">{{ setting.description }}</p>
          </div>
          <Checkbox 
            :model-value="setting.checked"
            :binary="true"
            :inputId="setting.id"
            :disabled="setting.disabled"
            @update:model-value="setting.onChange"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/dialog'
import Checkbox from 'primevue/checkbox'
import { useVisibilityStore } from './visibility.store'
import { useUserStore } from '@/authentication/user.store'

defineProps<{
  isOpen: boolean
}>()

defineEmits<{
  'update:isOpen': [value: boolean]
}>()

const visibilityStore = useVisibilityStore()
const userStore = useUserStore()

const settings = computed(() => [
  {
    id: 'betaModeCheckbox',
    label: 'Beta Mode',
    description: 'Enable beta features',
    checked: visibilityStore.betaVisibility,
    disabled: false,
    onChange: visibilityStore.toggleBetaMode
  },
  {
    id: 'editorModeCheckbox',
    label: 'Editor Mode',
    description: 'Enable editor features (admin only)',
    checked: visibilityStore.editorVisibility,
    disabled: !userStore.isSuperAdmin,
    onChange: visibilityStore.toggleEditorMode
  },
  {
    id: 'iodModeCheckbox',
    label: 'IOD Visibility',
    description: 'Override IOD feature visibility',
    checked: visibilityStore.iodVisibility,
    disabled: false,
    onChange: visibilityStore.toggleIodMode
  },
  {
    id: 'effectsModeCheckbox',
    label: 'Effects Visibility',
    description: 'Override effects statements visibility',
    checked: visibilityStore.effectsVisibility,
    disabled: false,
    onChange: visibilityStore.toggleEffectsMode
  },
  {
    id: 'shareModeCheckbox',
    label: 'Share Visibility',
    description: 'Override share feature visibility',
    checked: visibilityStore.shareVisibility,
    disabled: false,
    onChange: visibilityStore.toggleShareMode
  },
  {
    id: 'threadsModeCheckbox',
    label: 'Threads Visibility',
    description: 'Override threads feature visibility',
    checked: visibilityStore.threadsVisibility,
    disabled: false,
    onChange: visibilityStore.toggleThreadsMode
  }
])
</script>
